/**
 * The Graph Network API Service
 *
 * Service layer for querying The Graph Network Subgraph via GraphQL.
 * Fetches delegator data, indexer info, and network statistics.
 *
 * API Endpoint: https://api.thegraph.com/subgraphs/name/graphprotocol/graph-network-arbitrum
 * Schema: https://github.com/graphprotocol/graph-network-subgraph
 */

import { callCoinGeckoAPI } from './api';
import { logger } from './logger';
import type {
  GraphDelegator,
  GraphIndexer,
  GraphNetworkStats,
  GraphEpoch,
  GraphNetworkSubgraphResponse,
  TheGraphWalletData,
  TheGraphDelegation,
  THEGRAPH_CONSTANTS,
} from '../types/thegraph.types';

// ============================================
// CONSTANTS
// ============================================

// Graph Network Subgraph ID (Arbitrum)
const GRAPH_NETWORK_SUBGRAPH_ID = 'DZz4kDTdmzWLWsV373w2bSmoar3umKKH9y82SUKr5qmp';

// Get API key from environment variable
const THEGRAPH_API_KEY = import.meta.env.VITE_THEGRAPH_API_KEY || '';

// Decentralized Gateway endpoint (requires API key)
const GRAPH_NETWORK_SUBGRAPH = THEGRAPH_API_KEY
  ? `https://gateway-arbitrum.network.thegraph.com/api/${THEGRAPH_API_KEY}/subgraphs/id/${GRAPH_NETWORK_SUBGRAPH_ID}`
  : '';

// Legacy hosted service (deprecated but may still work for some queries)
const GRAPH_NETWORK_SUBGRAPH_LEGACY = 'https://api.thegraph.com/subgraphs/name/graphprotocol/graph-network-arbitrum';

// GRT decimals
const GRT_DECIMALS = 18;

// Request timeout
const REQUEST_TIMEOUT_MS = 30000;

// Cache duration (5 minutes)
const CACHE_DURATION_MS = 5 * 60 * 1000;

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Convert BigInt string to number (with decimals)
 */
const fromWei = (value: string | undefined, decimals: number = GRT_DECIMALS): number => {
  if (!value) return 0;
  try {
    const num = BigInt(value);
    const divisor = BigInt(10 ** decimals);
    const intPart = num / divisor;
    const fracPart = num % divisor;
    return Number(intPart) + Number(fracPart) / Number(divisor);
  } catch {
    return 0;
  }
};

/**
 * Convert reward cut from protocol format (0-1000000) to percentage (0-100)
 */
const cutToPercentage = (cut: number): number => {
  return cut / 10000; // 1000000 = 100%
};

/**
 * Validate Ethereum address
 */
export const isValidEthereumAddress = (address: string): boolean => {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

/**
 * Check if API key is configured
 */
export const isApiKeyConfigured = (): boolean => {
  return !!THEGRAPH_API_KEY;
};

/**
 * Execute GraphQL query
 */
const executeQuery = async <T>(
  query: string,
  variables: Record<string, unknown> = {}
): Promise<T> => {
  // Determine which endpoint to use
  const endpoint = THEGRAPH_API_KEY ? GRAPH_NETWORK_SUBGRAPH : GRAPH_NETWORK_SUBGRAPH_LEGACY;

  if (!endpoint) {
    throw new Error('The Graph API key not configured. Please set VITE_THEGRAPH_API_KEY environment variable.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ query, variables }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // If legacy endpoint returns 410 Gone or similar, it means it's deprecated
      if (response.status === 410 || response.status === 404) {
        throw new Error('The Graph hosted service is deprecated. Please configure VITE_THEGRAPH_API_KEY for the decentralized gateway.');
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors[0].message);
    }

    return result.data as T;
  } catch (error: unknown) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw error;
  }
};

// ============================================
// GRAPHQL QUERIES
// ============================================

const QUERIES = {
  // Get delegator with all stakes
  DELEGATOR: `
    query GetDelegator($id: ID!) {
      delegator(id: $id) {
        id
        totalStakedTokens
        totalUnstakedTokens
        totalRealizedRewards
        stakesCount
        createdAt
        stakes(first: 100, orderBy: stakedTokens, orderDirection: desc) {
          id
          stakedTokens
          unstakedTokens
          lockedTokens
          lockedUntil
          realizedRewards
          createdAt
          shareAmount
          personalExchangeRate
          indexer {
            id
            stakedTokens
            delegatedTokens
            delegatorShares
            delegationExchangeRate
            indexingRewardCut
            queryFeeCut
            url
            defaultDisplayName
          }
        }
      }
    }
  `,

  // Get network stats
  NETWORK_STATS: `
    query GetNetworkStats {
      graphNetwork(id: "1") {
        id
        totalSupply
        totalTokensStaked
        totalDelegatedTokens
        totalTokensAllocated
        totalTokensSignalled
        totalIndexingRewards
        totalQueryFees
        currentEpoch
        epochLength
        networkGRTIssuancePerBlock
        delegationRatio
        delegationTaxPercentage
        minimumIndexerStake
        thawingPeriod
        curationPercentage
        protocolFeePercentage
      }
    }
  `,

  // Get top indexers
  TOP_INDEXERS: `
    query GetTopIndexers($first: Int!, $skip: Int!) {
      indexers(
        first: $first
        skip: $skip
        orderBy: delegatedTokens
        orderDirection: desc
        where: { stakedTokens_gt: "100000000000000000000000" }
      ) {
        id
        stakedTokens
        delegatedTokens
        allocatedTokens
        delegatorShares
        delegationExchangeRate
        indexingRewardCut
        queryFeeCut
        url
        defaultDisplayName
      }
    }
  `,

  // Get single indexer
  INDEXER: `
    query GetIndexer($id: ID!) {
      indexer(id: $id) {
        id
        stakedTokens
        delegatedTokens
        allocatedTokens
        unstakedTokens
        lockedTokens
        tokensLockedUntil
        delegatorShares
        delegationExchangeRate
        indexingRewardCut
        queryFeeCut
        url
        geoHash
        defaultDisplayName
        createdAt
      }
    }
  `,

  // Get recent epochs for APY calculation (using graphNetworks for historical data)
  RECENT_EPOCHS: `
    query GetRecentEpochs {
      graphNetwork(id: "1") {
        id
        totalIndexingRewards
        totalDelegatedTokens
        currentEpoch
        epochLength
        networkGRTIssuancePerBlock
      }
    }
  `,
};

// ============================================
// API SERVICE
// ============================================

class TheGraphAPIService {
  private cache: Map<string, { data: unknown; timestamp: number }> = new Map();

  /**
   * Get cached data or fetch fresh
   */
  private async getCached<T>(
    key: string,
    fetcher: () => Promise<T>,
    duration: number = CACHE_DURATION_MS
  ): Promise<T> {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < duration) {
      return cached.data as T;
    }

    const data = await fetcher();
    this.cache.set(key, { data, timestamp: Date.now() });
    return data;
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get GRT price from CoinGecko
   */
  async getGrtPrice(): Promise<number> {
    try {
      return await this.getCached('grt_price', async () => {
        const data = await callCoinGeckoAPI<{ 'the-graph': { usd: number } }>(
          '/simple/price?ids=the-graph&vs_currencies=usd'
        );
        return data?.['the-graph']?.usd || 0;
      }, 60000); // 1 minute cache for price
    } catch (error) {
      logger.error('TheGraphAPI', 'Failed to fetch GRT price', error);
      return 0;
    }
  }

  /**
   * Get network statistics
   */
  async getNetworkStats(): Promise<GraphNetworkStats | null> {
    try {
      return await this.getCached('network_stats', async () => {
        const result = await executeQuery<{ graphNetwork: GraphNetworkStats }>(
          QUERIES.NETWORK_STATS
        );
        return result.graphNetwork;
      });
    } catch (error) {
      logger.error('TheGraphAPI', 'Failed to fetch network stats', error);
      return null;
    }
  }

  /**
   * Get delegator data by address
   */
  async getDelegator(address: string): Promise<GraphDelegator | null> {
    if (!isValidEthereumAddress(address)) {
      logger.warn('TheGraphAPI', `Invalid address format: ${address}`);
      return null;
    }

    try {
      console.log('TheGraphAPI', `Fetching delegator ${address.toLowerCase()}...`);
      const result = await executeQuery<{ delegator: GraphDelegator | null }>(
        QUERIES.DELEGATOR,
        { id: address.toLowerCase() }
      );
      console.log('TheGraphAPI', `Delegator result:`, result.delegator ? 'found' : 'not found');
      return result.delegator;
    } catch (error) {
      logger.error('TheGraphAPI', `Failed to fetch delegator ${address}`, error);
      // Re-throw the error so it can be displayed to the user
      throw error;
    }
  }

  /**
   * Get indexer data by address
   */
  async getIndexer(address: string): Promise<GraphIndexer | null> {
    if (!isValidEthereumAddress(address)) {
      return null;
    }

    try {
      const result = await executeQuery<{ indexer: GraphIndexer | null }>(
        QUERIES.INDEXER,
        { id: address.toLowerCase() }
      );
      return result.indexer;
    } catch (error) {
      logger.error('TheGraphAPI', `Failed to fetch indexer ${address}`, error);
      return null;
    }
  }

  /**
   * Get top indexers
   */
  async getTopIndexers(limit: number = 50): Promise<GraphIndexer[]> {
    try {
      return await this.getCached(`top_indexers_${limit}`, async () => {
        const result = await executeQuery<{ indexers: GraphIndexer[] }>(
          QUERIES.TOP_INDEXERS,
          { first: limit, skip: 0 }
        );
        return result.indexers || [];
      });
    } catch (error) {
      logger.error('TheGraphAPI', 'Failed to fetch top indexers', error);
      return [];
    }
  }

  /**
   * Calculate estimated network APY
   * Uses network issuance rate and delegation ratio to estimate APY
   */
  async calculateNetworkApy(): Promise<number> {
    try {
      const networkStats = await this.getNetworkStats();

      if (!networkStats) {
        return 0;
      }

      // Get total delegated tokens
      const totalDelegated = fromWei(networkStats.totalDelegatedTokens);
      if (totalDelegated === 0) return 0;

      // Calculate APY based on network GRT issuance
      // The network issues GRT per block, and delegators get a share based on delegation ratio
      const issuancePerBlock = fromWei(networkStats.networkGRTIssuancePerBlock);
      const blocksPerYear = 365 * 24 * 60 * 60 / 12; // ~12 second blocks on Arbitrum
      const yearlyIssuance = issuancePerBlock * blocksPerYear;

      // Delegators get a portion based on delegation parameters
      // Typically around 10-15% APY for delegators after indexer cuts
      const delegationRatio = parseInt(networkStats.delegationRatio || '16') / 1000000;
      const totalStaked = fromWei(networkStats.totalTokensStaked);

      // Estimate delegator share of rewards (roughly 50% goes to delegators)
      const delegatorShareOfRewards = 0.5;
      const estimatedDelegatorRewards = yearlyIssuance * delegatorShareOfRewards;

      // APY = yearly rewards / total delegated
      const apy = (estimatedDelegatorRewards / totalDelegated) * 100;

      // Return a reasonable APY (typically 8-15% for The Graph)
      return Math.min(Math.max(apy, 5), 25); // Cap between 5-25% for sanity
    } catch (error) {
      logger.error('TheGraphAPI', 'Failed to calculate APY', error);
      // Return a default reasonable APY if calculation fails
      return 10;
    }
  }

  /**
   * Calculate estimated APY for a specific indexer
   */
  calculateIndexerApy(indexer: GraphIndexer, networkApy: number): number {
    // Base APY adjusted by indexer's reward cut
    const indexerCut = cutToPercentage(indexer.indexingRewardCut);
    const delegatorShare = 100 - indexerCut;

    // Adjust APY based on delegator share
    const estimatedApy = (networkApy * delegatorShare) / 100;

    return Math.max(0, estimatedApy);
  }

  /**
   * Get complete wallet data for display
   */
  async getWalletData(address: string): Promise<TheGraphWalletData | null> {
    if (!isValidEthereumAddress(address)) {
      return null;
    }

    try {
      const [delegator, grtPrice, networkApy] = await Promise.all([
        this.getDelegator(address),
        this.getGrtPrice(),
        this.calculateNetworkApy(),
      ]);

      // If no delegator data, return empty wallet (no delegations found)
      if (!delegator) {
        console.log('TheGraphAPI', `No delegator data found for ${address}`);
        return {
          address: address.toLowerCase(),
          grtBalance: 0,
          grtBalanceUsd: 0,
          totalDelegated: 0,
          totalDelegatedUsd: 0,
          totalUnstaking: 0,
          totalLocked: 0,
          totalRealizedRewards: 0,
          totalRealizedRewardsUsd: 0,
          pendingRewards: 0,
          pendingRewardsUsd: 0,
          delegationsCount: 0,
          delegations: [],
          firstDelegatedAt: null,
          lastDelegatedAt: null,
        };
      }

      console.log('TheGraphAPI', `Delegator found: ${delegator.id}, stakes: ${delegator.stakesCount}`);

      // Process delegations
      const delegations: TheGraphDelegation[] = (delegator.stakes || []).map((stake) => {
        const indexer = stake.indexer;
        const stakedTokens = fromWei(stake.stakedTokens);
        const lockedTokens = fromWei(stake.lockedTokens);
        const realizedRewards = fromWei(stake.realizedRewards);

        // Calculate estimated pending rewards based on share and exchange rate
        const personalRate = parseFloat(stake.personalExchangeRate || '1');
        const currentRate = parseFloat(indexer.delegationExchangeRate || '1');
        const shareAmount = fromWei(stake.shareAmount);
        const estimatedCurrentValue = shareAmount * currentRate;
        const estimatedPendingRewards = Math.max(0, estimatedCurrentValue - stakedTokens);

        return {
          id: stake.id,
          indexer: {
            id: indexer.id,
            name: indexer.defaultDisplayName,
            url: indexer.url,
            indexingRewardCut: cutToPercentage(indexer.indexingRewardCut),
            queryFeeCut: cutToPercentage(indexer.queryFeeCut),
            totalDelegated: fromWei(indexer.delegatedTokens),
            ownStake: fromWei(indexer.stakedTokens),
            delegatorCount: undefined, // Not available in current schema
            allocationsCount: undefined, // Not available in current schema
          },
          stakedTokens,
          stakedTokensUsd: stakedTokens * grtPrice,
          unstakedTokens: fromWei(stake.unstakedTokens),
          lockedTokens,
          lockedUntil: stake.lockedUntil > 0 ? new Date(stake.lockedUntil * 1000) : null,
          realizedRewards,
          realizedRewardsUsd: realizedRewards * grtPrice,
          shareAmount,
          personalExchangeRate: personalRate,
          currentExchangeRate: currentRate,
          estimatedPendingRewards,
          estimatedApy: this.calculateIndexerApy(indexer, networkApy),
        };
      });

      // Aggregate stats
      const totalDelegated = delegations.reduce((sum, d) => sum + d.stakedTokens, 0);
      const totalUnstaking = delegations.reduce((sum, d) => sum + d.unstakedTokens, 0);
      const totalLocked = delegations.reduce((sum, d) => sum + d.lockedTokens, 0);
      const totalRealizedRewards = fromWei(delegator.totalRealizedRewards);

      // Sum up pending rewards from all delegations
      const pendingRewards = delegations.reduce((sum, d) => sum + (d.estimatedPendingRewards || 0), 0);

      return {
        address: delegator.id,
        grtBalance: 0, // Would need separate on-chain query
        grtBalanceUsd: 0,
        totalDelegated,
        totalDelegatedUsd: totalDelegated * grtPrice,
        totalUnstaking,
        totalLocked,
        totalRealizedRewards,
        totalRealizedRewardsUsd: totalRealizedRewards * grtPrice,
        pendingRewards,
        pendingRewardsUsd: pendingRewards * grtPrice,
        delegationsCount: delegations.length,
        delegations,
        firstDelegatedAt: delegator.createdAt ? new Date(delegator.createdAt * 1000) : null,
        lastDelegatedAt: delegator.lastDelegatedAt
          ? new Date(delegator.lastDelegatedAt * 1000)
          : null,
      };
    } catch (error) {
      logger.error('TheGraphAPI', `Failed to get wallet data for ${address}`, error);
      return null;
    }
  }

  /**
   * Format address for display
   */
  formatAddress(address: string): string {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }

  /**
   * Get explorer URL for address
   */
  getExplorerUrl(address: string, type: 'delegator' | 'indexer' = 'delegator'): string {
    return `https://thegraph.com/explorer/profile/${address}?view=${type}s`;
  }

  /**
   * Get Etherscan URL
   */
  getEtherscanUrl(address: string): string {
    return `https://arbiscan.io/address/${address}`;
  }
}

// Export singleton
export const thegraphAPI = new TheGraphAPIService();
